import { useMediaQuery } from 'react-responsive'
import React, { useState } from 'react'
import moment from 'moment-timezone'
import difference from 'lodash/difference'
import isNil from 'lodash/isNil'
import cx from 'classnames'

import { Radio, RadioChangeEvent, SelectableCardGrid, CardGridSize } from '@commutifi-fe/ui'
import { DaysValueEnum, DaysValues, Frequency } from 'shared/interfaces/Commute'
import { FormItemInjectedProps } from 'shared/interfaces'
import { screenSizes } from 'constants/global'
import { DAYS_PER_WEEK } from 'constants/dates'
import { FormattedMessage } from 'locales/index'

import FrequencySelector from './FrequencySelector'
import styles from './styles.module.scss'

const { ClickableCard } = SelectableCardGrid

const dayPickerFrequencyValidation = (
  rule: any,
  value: Frequency,
  errCallback: any,
  { min, max }: { min: number; max: number }
) => {
  // This case will only happen if there is no more days available to pick from.
  // Ex. the user says he commutes 5 days and that he works from home 5 days. Then in
  // his secondary commute we need to display this error
  if (max === 0) {
    // eslint-disable-next-line prefer-promise-reject-errors -- Was here, feel free to change
    return Promise.reject(<FormattedMessage id="dayPicker.validation.noMoreDaysAvailable" />)
  }

  if (value && (value.weeklyFrequency < min || Math.floor(value.weeklyFrequency) > max)) {
    // eslint-disable-next-line prefer-promise-reject-errors -- Was here, feel free to change
    return Promise.reject(
      min === max ? (
        // Instead of displaying a weird message with a range that has the same start and end value
        <FormattedMessage id="required" />
      ) : (
        <FormattedMessage
          id="dayPicker.validation.range"
          values={{
            min,
            max
          }}
        />
      )
    )
  }

  return Promise.resolve()
}

const dayPickerRequiredValidation = (rule: any, value: Frequency) => {
  if (!value || (value && isNil(value.weeklyFrequency))) {
    // eslint-disable-next-line prefer-promise-reject-errors -- Was here, feel free to change
    return Promise.reject(<FormattedMessage id="required" />)
  }
  return Promise.resolve()
}

export const getDayPickerValidation = (
  minWeeklyFrequency: number | undefined | null,
  maxWeeklyFrequency: number | undefined | null,
  isRequired = true
) => {
  const rules = [
    {
      validateTrigger: ['onChange', 'onBlur'],
      validator: (rule: any, value: Frequency, errCallback: any) =>
        dayPickerFrequencyValidation(rule, value, errCallback, {
          min: isNil(minWeeklyFrequency) ? 1 : minWeeklyFrequency,
          max: isNil(maxWeeklyFrequency) ? DAYS_PER_WEEK : maxWeeklyFrequency
        })
    }
  ]

  isRequired &&
    rules.push({
      validateTrigger: ['onBlur'],
      validator: (rule: any, value: Frequency) => dayPickerRequiredValidation(rule, value)
    })
  return { rules }
}

export interface DayPickerProps extends FormItemInjectedProps<Frequency> {
  className?: string
  initialValue?: Frequency
  // If we should allow the user to input a frequency (adds a toggle) or the user can only pick days of the week
  allowFrequency?: boolean
  disabledOptions?: DaysValues[]
  // If you don't want to present all the week days to the user
  // you can select a subset of days
  daysSubset?: DaysValues[]
  // Initial value for isSetDays
  initialIsSetDays?: boolean
  // Controlled value for isSetDays
  setDays?: boolean
  // When you don't want to restrict the user to set days, you can set a maximum weekly frequency value
  // that will be calculate to all frequency use cases (weekly, monthly, etc)
  maxWeeklyFrequency?: number
  minWeeklyFrequency?: number
}

export function DayPicker({
  className,
  onChange,
  initialValue,
  id,
  value,
  allowFrequency = false,
  disabledOptions,
  daysSubset,
  initialIsSetDays,
  setDays,
  maxWeeklyFrequency,
  minWeeklyFrequency = 1
}: DayPickerProps) {
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })
  // Make sure to keep daysOptions INSIDE the component so it updates when we change moment language
  const daysOptions = React.useMemo(
    () => [
      {
        label: moment.weekdaysShort(1),
        value: DaysValueEnum.Monday
      },
      {
        label: moment.weekdaysShort(2),
        value: DaysValueEnum.Tuesday
      },
      {
        label: moment.weekdaysShort(3),
        value: DaysValueEnum.Wednesday
      },
      {
        label: moment.weekdaysShort(4),
        value: DaysValueEnum.Thursday
      },
      {
        label: moment.weekdaysShort(5),
        value: DaysValueEnum.Friday
      },
      {
        label: moment.weekdaysShort(6),
        value: DaysValueEnum.Saturday
      },
      {
        label: moment.weekdaysShort(7),
        value: DaysValueEnum.Sunday
      }
    ],
    []
  )
  const [cachedDays, setCachedDays] = useState(value?.days)
  const daysDisplayed = React.useMemo(
    () =>
      daysSubset && daysSubset.length > 0
        ? daysOptions.filter((dayOption) => daysSubset.includes(dayOption.value))
        : daysOptions,
    [daysOptions, daysSubset]
  )
  const maxFrequency = isNil(maxWeeklyFrequency) ? daysDisplayed.length : maxWeeklyFrequency

  const setDaysOption = 'setDays'
  const noSetDaysOption = 'noSetDays'
  // If the value is prefilled and we have some days set (even if 0 day is selected)
  // We want this radio button to be set to setDays.
  const [isSetDays, setIsSetDays] = useState(initialIsSetDays !== undefined ? initialIsSetDays : Boolean(value?.days))
  const controlledSetDays = setDays !== undefined ? setDays : isSetDays

  const currentDaysValue = value?.days
  const currentWeeklyFrequency = value?.weeklyFrequency

  /* --- This effect takes care of making sure the value of this component stay consistent and valid --- */
  React.useEffect(() => {
    // When we restrict the component to a subset of days, if that subset is dynamic
    // we need to make sure we always have a valid selection of days. To do that we need
    // to check the difference between the current array of selected days and the subset
    if (currentDaysValue && currentDaysValue.length > 0 && daysSubset) {
      const invalidDaysValues = difference(currentDaysValue, daysSubset)
      if (invalidDaysValues.length > 0) {
        handleDaysChange([])
      }
    } else if (isNil(currentDaysValue) && currentDaysValue === false && daysSubset) {
      // If this day picker depends on another one we set the days to an empty
      // array to indicate that we want the user to select setDays instead of a frequency
      // which is less precise
      handleDaysChange([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy, keep to be safe
  }, [daysSubset, currentDaysValue, currentWeeklyFrequency, maxFrequency])

  const handleSetDaysOnChange = (e: RadioChangeEvent) => {
    const toggledIsSetDays = e.target.value === setDaysOption

    // If the user was on the option 'No set days' and toggles to 'Set days', we want to
    // reset to the default value if there was a value on component mount
    if (toggledIsSetDays && toggledIsSetDays !== controlledSetDays) {
      handleDaysChange(cachedDays || [])
    }

    // If the user toggles to 'No set days' and there is a value set by default, there is a change he will never
    // edit the value anymore. For that reason we reset the selected days to be sure everything is coherent
    if (!toggledIsSetDays && currentWeeklyFrequency) {
      setCachedDays(currentDaysValue)
      onChange && onChange({ weeklyFrequency: currentWeeklyFrequency })
    }
    setIsSetDays(toggledIsSetDays)
  }

  const handleDaysChange = (days: DaysValues[]) => {
    if (onChange) {
      onChange({ days, weeklyFrequency: days.length })
    }
  }

  const handleFrequencyChange = (frequency: Frequency) => {
    setCachedDays(undefined)
    if (onChange) {
      onChange({ ...frequency, days: undefined })
    }
  }

  return (
    <div id={id} className={cx(styles.container, className)}>
      {allowFrequency ? (
        <Radio.Group
          value={controlledSetDays ? setDaysOption : noSetDaysOption}
          onChange={handleSetDaysOnChange}
          buttonStyle="solid"
          className="u-margin-bottom--medium"
          block
        >
          <Radio.Button value={setDaysOption} data-testid="radio-button-set-days">
            <FormattedMessage id="dayPicker.setDays" />
          </Radio.Button>
          <Radio.Button value={noSetDaysOption} data-testid="radio-button-no-set-days">
            <FormattedMessage id="dayPicker.noSetDays" />
          </Radio.Button>
        </Radio.Group>
      ) : null}
      {controlledSetDays ? (
        <SelectableCardGrid
          // If there is no frequency chosen we set to default week days
          initialSelectedCards={!currentWeeklyFrequency ? initialValue?.days : []}
          selectedCards={currentDaysValue}
          onChange={handleDaysChange}
          cardSize={isXSmall ? CardGridSize.Small : CardGridSize.Default}
          multiSelect
          equalWidth
        >
          {daysDisplayed.map((day) => (
            <ClickableCard
              key={day.value}
              id={day.value}
              title={day.label}
              disabled={disabledOptions?.includes(day.value)}
            />
          ))}
        </SelectableCardGrid>
      ) : (
        <FrequencySelector
          onChange={handleFrequencyChange}
          value={{
            weeklyFrequency: currentWeeklyFrequency
          }}
          initialValue={initialValue}
          minWeeklyFrequency={minWeeklyFrequency}
          maxWeeklyFrequency={maxFrequency}
        />
      )}
    </div>
  )
}

export default DayPicker
